import { AlertBanner, CustomButton } from '@/shared/ui/components';
import { withModalLayout } from '@/shared/lib';
import classNames from 'classnames';
import { t } from 'ttag';
import CreateReturnShippingInfo from '../createReturnShippingInfo/createReturnShippingInfo';
import { SIGNATURE_OPTION } from './previewShipmentModal.constants';
import { usePreviewShipmentModal } from './usePreviewShipmentModal';
import type { PreviewShipmentModalProps } from './previewShipmentModal.types';
import './previewShipmentModal.scss';

/**
 * Read-only review step shown before purchasing a return shipping label. Renders the sender and recipient cards through CreateReturnShippingInfo with editing hidden, lists shipment date, packet dimension, weight, hazardous-materials and signature-confirmation rows taken from either the passed package or the rate resolved by usePreviewShipmentModal, shows the carrier rate and price, and purchases via onSubmit.
 *
 * @param props - Shipment data, state lists, class name and the shipping-label flag selecting between the entered package and the selected rate.
 * @returns The preview shipment modal element.
 */
const PreviewShipmentModal = withModalLayout(((props: PreviewShipmentModalProps) => {
  const { state, onSubmit, selectedRateData } = usePreviewShipmentModal(props);

  return (
    <div className={classNames('preview-shipment-modal', props.className)}>
      <div className="custom-modal-body-container">
        <div className="part-label">
          <AlertBanner
            alertType="warning"
            description={t`Please review the label information you entered before purchase the shipping label.`}
          />
        </div>
        <div className="part-info">
          <div className="sender">
            <CreateReturnShippingInfo
              className="--small"
              isSender
              data={props.data.senderContact}
              listStatesCA={props.data.listStatesCA}
              listStatesUS={props.data.listStatesUS}
              isHideEdit
            />
          </div>
          <div className="recipient">
            <CreateReturnShippingInfo
              className="--small"
              data={props.data.recipientContact}
              listStatesCA={props.data.listStatesCA}
              listStatesUS={props.data.listStatesUS}
              isHideEdit
            />
          </div>
          <div className="package">
            <div className="title">{t`Package Information`}</div>
            <div className="body">
              <div className="row-modal">
                <div>{t`Shipment date:`}</div>
                <div>{props.data.shipmentDate.format('MM/DD/YYYY')}</div>
              </div>
              <div className="row-modal">
                <div>{t`Packet dimension:`}</div>
                <div>
                  {props.isShippingLabel ? props.data.package?.name : selectedRateData?.name}
                </div>
              </div>
              <div className="row-modal">
                <div>{`${t`Weight`} (lb):`}</div>
                <div>{props.isShippingLabel ? props.data.weight : selectedRateData?.weight}</div>
              </div>
              <div className="row-modal">
                <div>{t`Hazardous materials:`}</div>
                <div>
                  {props.data?.dangerousGoods
                    ? t`Contains hazardous materials`
                    : t`No hazardous materials`}
                </div>
              </div>
              <div className="row-modal">
                <div>{t`Signature confirmation:`}</div>
                <div>
                  {props.data?.signatureConfirmation
                    ? SIGNATURE_OPTION[props.data.signatureConfirmation]
                    : t`Not required`}
                </div>
              </div>
            </div>
          </div>
          <div className="rate">
            <div className="title">{t`Rate`}</div>
            <div className="body">
              <div className="row-modal">
                <div>{`${selectedRateData?.carrier} ${selectedRateData?.serviceLevelName}`}</div>
                <div className="bold">{`$ ${parseFloat(selectedRateData?.amount).toFixed(2)}`}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="part-footer">
        <CustomButton
          id="cancel-btn"
          onClick={props.onCancel}
          className="cancel-button"
          disabled={state.isSaving}
          style="neutral"
          type="outline"
          size="middle"
        >
          {t`Cancel`}
        </CustomButton>
        <CustomButton
          id="save-btn"
          loading={state.isSaving}
          onClick={onSubmit}
          className="save-button"
          type="primary"
          size="middle"
        >
          {t`Purchase`}
        </CustomButton>
      </div>
    </div>
  );
}) as any);

(PreviewShipmentModal as any).defaultProps = {
  className: undefined,
  title: t`Review return shipping label`,
};

export default PreviewShipmentModal;
